/**
 * Computes display name / date range / location when the user merges multiple
 * DetectedTrip rows locally. Receipt-style emails (itinerary + receipt without a
 * real destination) must not outweigh real activities (majority Tokyo, May).
 */

import type { DetectedTrip } from '@/services/tripDetection';

const MS_PER_DAY = 86400000;
/** Align with functions/src/services/tripDetectionSanitize.ts */
const MAX_DETECTED_TRIP_SPAN_DAYS = 120;

export type LooseBooking = DetectedTrip['bookings'][number];

function coerceDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : v;
  }
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function stripEmoji(s: string): string {
  return s.replace(/[\u{1F300}-\u{1F9FF}]|[\u2600-\u26FF]/gu, '').trim();
}

/**
 * Receipt / itinerary PDF rows that duplicate real bookings but pollute bounds + naming.
 */
export function isNoiseBookingForMergedTripMetadata(booking: LooseBooking): boolean {
  const locRaw = typeof booking.location === 'string' ? stripEmoji(booking.location) : '';
  const loc = locRaw.trim().toLowerCase();
  const subj =
    typeof (booking as { rawEmailSubject?: string }).rawEmailSubject === 'string'
      ? (booking as { rawEmailSubject?: string }).rawEmailSubject!.toLowerCase()
      : '';

  if (/receipt confirmation|confirmation,\s*[x]{2,}|[x]{2,}y|placeholder/.test(loc)) return true;

  if (loc === 'unknown' || loc.startsWith('unknown,')) return true;

  if (!loc && /itinerary|e-?ticket|e\s*ticket/.test(subj) && /receipt|confirmation/.test(subj)) return true;

  if (/e\s*ticket|e-?ticket|itinerary/.test(subj) && /receipt|invoice/.test(subj)) {
    const firstSeg = loc.split(',')[0]?.trim() ?? '';
    if (/receipt|confirm|booking/.test(loc) || firstSeg.length <= 12) return true;
  }

  return false;
}

function pushTravelDates(booking: LooseBooking, out: Date[]): void {
  const keys = ['departureDate', 'arrivalDate', 'checkInDate', 'checkOutDate'] as const;
  for (const k of keys) {
    const d = coerceDate((booking as Record<string, unknown>)[k]);
    if (d) out.push(d);
  }
}

function collectTravelDates(bookings: LooseBooking[]): Date[] {
  const dates: Date[] = [];
  for (const b of bookings) {
    pushTravelDates(b, dates);
  }
  return dates;
}

/**
 * Same outlier strategy as server `computeBoundedTripDates` (8th–92nd percentile).
 */
function boundedFromDateList(
  dates: Date[],
  fallbackStart: Date,
  fallbackEnd: Date,
): { start: Date; end: Date; clamped: boolean } {
  if (dates.length === 0) {
    const span = (fallbackEnd.getTime() - fallbackStart.getTime()) / MS_PER_DAY;
    if (span > MAX_DETECTED_TRIP_SPAN_DAYS) {
      return {
        start: fallbackStart,
        end: new Date(fallbackStart.getTime() + MAX_DETECTED_TRIP_SPAN_DAYS * MS_PER_DAY),
        clamped: true,
      };
    }
    return { start: fallbackStart, end: fallbackEnd, clamped: false };
  }

  dates.sort((a, b) => a.getTime() - b.getTime());
  const n = dates.length;
  const lo = dates[Math.min(Math.floor(n * 0.08), n - 1)];
  const hi = dates[Math.max(Math.ceil(n * 0.92) - 1, 0)];

  let start = lo;
  let end = hi;
  if (end < start) {
    start = dates[0];
    end = dates[n - 1];
  }

  let clamped = false;
  const spanDays = (end.getTime() - start.getTime()) / MS_PER_DAY;
  if (spanDays > MAX_DETECTED_TRIP_SPAN_DAYS) {
    end = new Date(start.getTime() + MAX_DETECTED_TRIP_SPAN_DAYS * MS_PER_DAY);
    clamped = true;
  }

  return { start, end, clamped };
}

function normalizeCityKey(location: string | undefined): string | null {
  if (!location) return null;
  const s = stripEmoji(location).trim().toLowerCase();
  if (s.length < 2) return null;
  if (/receipt|unknown|^xx|placeholder|confirm only/.test(s)) return null;
  const first = s.split(',')[0].trim();
  if (!first || first === 'unknown') return null;
  return first.replace(/\s+japan$|\s+usa$/i, '').trim() || null;
}

function pluralityDisplayCity(bookings: LooseBooking[]): string | null {
  const counts = new Map<string, number>();
  for (const b of bookings) {
    if (isNoiseBookingForMergedTripMetadata(b)) continue;
    const key = normalizeCityKey(b.location as string | undefined);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  let winnerKey: string | null = null;
  let max = 0;
  for (const [city, count] of counts) {
    if (count > max) {
      max = count;
      winnerKey = city;
    }
  }
  if (winnerKey == null || winnerKey === '') return null;
  return winnerKey.charAt(0).toUpperCase() + winnerKey.slice(1);
}

/** Winning full location label (e.g. "Tokyo, Japan") from majority vote on non-noise rows. */
function pluralityFullLocation(bookings: LooseBooking[]): string | undefined {
  const counts = new Map<string, number>();
  for (const b of bookings) {
    if (isNoiseBookingForMergedTripMetadata(b)) continue;
    const loc = stripEmoji((typeof b.location === 'string' ? b.location : '') || '').trim();
    if (!loc || /^unknown$/i.test(loc) || /receipt confirmation/i.test(loc)) continue;
    counts.set(loc, (counts.get(loc) ?? 0) + 1);
  }
  let best: string | undefined;
  let max = 0;
  counts.forEach((c, loc) => {
    if (c > max) {
      max = c;
      best = loc;
    }
  });
  return best;
}

/** Bookings whose location string mentions the plurality destination (helps drop orphan receipt rows when span is huge). */
function bookingsMatchingCity(bookings: LooseBooking[], cityKey: string | null): LooseBooking[] {
  if (!cityKey) return [];
  const needle = cityKey.toLowerCase();
  return bookings.filter((b) => {
    const loc = stripEmoji((typeof b.location === 'string' ? b.location : '') || '').toLowerCase();
    return loc.includes(needle);
  });
}

function formatMergedSuggestedName(primaryCityDisplay: string | null, start: Date, mergeCountHint: boolean): string {
  const suffix = mergeCountHint ? ' (Merged)' : '';
  const month = start.toLocaleString('en-US', { month: 'long' });
  const year = start.getFullYear();
  if (primaryCityDisplay) {
    return `${primaryCityDisplay} Trip ${month} ${year}${suffix}`;
  }
  return `Merged trip ${month} ${year}${suffix}`;
}

export function deriveMergedDetectedTripPresentation(input: {
  bookings: LooseBooking[];
  fallbackFromTripsToMerge: { startDate: string; endDate: string }[];
  /** Prefer a non–"Unknown …" label from one of the merged rows when location vote is weak. */
  fallbackTripSuggestedNames?: string[];
}): { suggestedName: string; startDate: string; endDate: string; location?: string } {
  const { bookings, fallbackFromTripsToMerge, fallbackTripSuggestedNames } = input;

  const fallbackStart =
    fallbackFromTripsToMerge.length > 0
      ? new Date(
          Math.min(...fallbackFromTripsToMerge.map((t) => new Date(t.startDate).getTime())),
        )
      : new Date();
  const fallbackEnd =
    fallbackFromTripsToMerge.length > 0
      ? new Date(
          Math.max(...fallbackFromTripsToMerge.map((t) => new Date(t.endDate).getTime())),
        )
      : new Date();

  let pool = bookings.filter((b) => !isNoiseBookingForMergedTripMetadata(b));
  if (pool.length === 0) pool = [...bookings];

  let bounds = boundedFromDateList(collectTravelDates(pool), fallbackStart, fallbackEnd);

  const spanDaysPrimary = (bounds.end.getTime() - bounds.start.getTime()) / MS_PER_DAY;
  const cityFromVotes = pluralityDisplayCity(bookings);

  if (spanDaysPrimary > 100 && cityFromVotes) {
    const narrowed = bookingsMatchingCity(
      bookings.filter((b) => !isNoiseBookingForMergedTripMetadata(b)),
      cityFromVotes.toLowerCase(),
    );
    const narrowDates = collectTravelDates(narrowed.length > 0 ? narrowed : pool);
    if (narrowDates.length >= 1) {
      const alt = boundedFromDateList(narrowDates, fallbackStart, fallbackEnd);
      const altSpan = (alt.end.getTime() - alt.start.getTime()) / MS_PER_DAY;
      if (altSpan < spanDaysPrimary - 10 || spanDaysPrimary > 120) {
        bounds = alt;
      }
    }
  }

  const startDate = bounds.start.toISOString().split('T')[0];
  const endDate = bounds.end.toISOString().split('T')[0];

  const primaryLocationLabel = pluralityFullLocation(bookings);
  let suggestedName = formatMergedSuggestedName(cityFromVotes, bounds.start, true);

  if (!cityFromVotes && !primaryLocationLabel) {
    const goodTitle = fallbackTripSuggestedNames?.find(
      (n) => n && !/^unknown\b/i.test(n.trim()),
    );
    if (goodTitle) {
      const base = goodTitle.replace(/\s*\(merged\)\s*$/i, '').trim();
      suggestedName = `${base} (Merged)`;
    }
  }

  return {
    suggestedName,
    startDate,
    endDate,
    location: primaryLocationLabel,
  };
}
